// assets/documentation-controller.js

// ============================================
// 基础控制器：纯前端分页
// ============================================
class DocumentationController {
  constructor(container) {
    this.container = container;
    this.contentArea = container.querySelector('[data-content-area]');
    this.allItemElements = [];

    this.config = {
      searchUrl: container.dataset.searchUrl || '/search',
      searchView: container.dataset.searchView || 'documentation',
      defaultQuery: container.dataset.defaultQuery || '',
    };

    this.bindEvents();
    this.loadDefaultContent();
  }

  bindEvents() {
    // 监听菜单选中事件
    this.container.addEventListener('doc-menu:select', (e) => {
      this.search(e.detail.searchQuery || e.detail.title);
    });

    this.container.addEventListener('doc-search:search', (e) => {
      this.search(e.detail.query);
    });
    
    // 监听 input 框的 enter 事件
    this.container.querySelector('input[name="q"]')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.search(e.target.value);
      }
    });

    // 监听分页组件的 page-change 事件
    this.contentArea.addEventListener('page-change', (e) => {
      this.applyPage(e.detail.start, e.detail.end);
    });

    // 防止表单默认提交
    this.container.addEventListener('submit', (e) => {
      e.preventDefault();
    });
  }

  loadDefaultContent() {
    if (this.config.defaultQuery) {
      this.search(this.config.defaultQuery);
    }
  }

  async search(query) {
    this.currentQuery = query;
    
    this.showLoading();
    try {
      const url = `${this.config.searchUrl}?q=${encodeURIComponent(query)}&type=product&view=${this.config.searchView}`;
      const response = await fetch(url);
      const html = await response.text();
      
      this.render(html);
      this.initPagination();
    } catch (error) {
      this.showError();
    } finally {
      this.hideLoading();
    }
  }

  render(html) {
    this.contentArea.innerHTML = html;
  }

  initPagination() {
    // 收集数据项，更新分页组件
    this.allItemElements = Array.from(this.contentArea.querySelectorAll('[data-item]'));
    // this.applyPage(0, this.allItemElements.length);
    
    const pagination = this.contentArea.querySelector('client-pagination');
    if (pagination) {
      pagination.total = this.allItemElements.length;
      const pageSize = parseInt(pagination.getAttribute('page-size')) || 10;
      this.applyPage(0, pageSize);
    }
  }

  applyPage(start, end) {
    this.allItemElements.forEach((item, index) => {
      item.style.display = (index >= start && index < end) ? '' : 'none';
    });
  }

  showLoading() {
    this.contentArea.classList.add('search-loading');
  }

  hideLoading() {
    this.contentArea.classList.remove('search-loading');
  }

  showError() {
    this.contentArea.innerHTML = `<div class="error">Please try again later</div>`;
  }
}

// ============================================
// FAQ 控制器：前端过滤模式
// ============================================
class FAQController {
  constructor(container, faqData) {
    this.container = container;
    this.contentArea = container.querySelector('[data-content-area]');
    this.faqData = faqData;
    this.perPage = parseInt(container.dataset.pageSize) || 5;
    this.currentPage = 1;
    this.currentCategory = null;
    this.currentKeyword = '';
    this.filteredFaqs = [];

    this.defaultQuery = container.dataset.defaultQuery || '';

    this.initFuseSearch();
    this.bindEvents();
    this.loadDefaultContent();
  }

  initFuseSearch() {
    const allFaqs = this.getAllFaqs();
    
    const fuseOptions = {
      keys: [
        { name: 'question', weight: 0.5 },
        { name: 'answerText', weight: 0.3 },
        { name: 'products', weight: 0.2 }
      ],
      threshold: 0.2,
      ignoreLocation: true,
      includeScore: true,
      minMatchCharLength: 2
    };

    this.fuse = new Fuse(allFaqs, fuseOptions);
  }

  bindEvents() {
    this.container.addEventListener('doc-menu:select', (e) => {
      const category = e.detail.searchQuery || e.detail.title;
      this.filterByCategory(category);
    });

    this.container.addEventListener('doc-search:search', (e) => {
      this.search(e.detail.query);
    });

    this.container.querySelector('input[name="q"]')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.search(e.target.value);
      }
    });

    // 监听分页按钮点击（事件委托）
    this.contentArea.addEventListener('click', (e) => {
      const btn = e.target.closest('.pagination-btn');
      if (btn && !btn.disabled) {
        e.preventDefault();
        const page = parseInt(btn.dataset.page, 10);
        if (page) {
          this.goToPage(page);
        }
      }
    });

    this.container.addEventListener('submit', (e) => {
      e.preventDefault();
    });
  }

  goToPage(page) {
    this.currentPage = page;
    this.renderCurrentPage();
  }

  loadDefaultContent() {
    if (this.defaultQuery) {
      this.filterByCategory(this.defaultQuery);
    } else if (this.faqData.length > 0) {
      this.filterByCategory(this.faqData[0].category);
    }
  }

  filterByCategory(category) {
    this.currentCategory = category;
    this.currentKeyword = '';
    this.currentPage = 1;

    const categoryData = this.faqData.find(c => c.category === category);
    if (categoryData) {
      this.filteredFaqs = categoryData.faqs.map(faq => ({
        ...faq,
        category: categoryData.category,
        products: categoryData.products
      }));
    } else {
      this.filteredFaqs = [];
    }

    this.renderCurrentPage();
  }

  search(keyword) {
    this.currentKeyword = keyword.trim();
    this.currentPage = 1;

    if (!this.currentKeyword) {
      this.loadDefaultContent();
      return;
    } else {
      this.currentCategory = null;
      const results = this.fuse.search(this.currentKeyword);
      
      const maxScore = 0.8;
      const filteredResults = results.filter(result => result.score <= maxScore);
      
      this.filteredFaqs = filteredResults.map(result => result.item);
    }

    this.renderCurrentPage();
  }

  getAllFaqs() {
    return this.faqData.flatMap(category => 
      category.faqs.map(faq => ({
        ...faq,
        category: category.category,
        products: category.products
      }))
    );
  }

  paginate(data) {
    const total = data.length;
    const totalPages = Math.ceil(total / this.perPage);
    const start = (this.currentPage - 1) * this.perPage;
    const items = data.slice(start, start + this.perPage);

    return { items, total, totalPages };
  }

  renderCurrentPage() {
    const { items, total, totalPages } = this.paginate(this.filteredFaqs);

    if (items.length === 0) {
      this.contentArea.innerHTML = this.renderEmpty();
      return;
    }

    const faqListHtml = items.map(faq => this.renderFaqItem(faq)).join('');
    const paginationHtml = totalPages > 1 ? this.renderPagination(totalPages) : '';

    this.contentArea.innerHTML = `
      <div class="faq-list">${faqListHtml}</div>
      ${paginationHtml}
    `;
  }

  renderFaqItem(faq) {
    return `
      <div class="faq-item is-expanded">
        <h3 class="faq-question">${faq.question}</h3>
        <div class="faq-answer">
          <div class="faq-answer-content">${faq.answerHtml}</div>
        </div>
      </div>
    `;
  }

  renderPagination(totalPages) {
    let pages = '';
    
    // 显示页码逻辑：最多显示 5 个页码
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    for (let i = start; i <= end; i++) {
      const isActive = i === this.currentPage;
      pages += `
        <button class="pagination-btn ${isActive ? 'is-active' : ''}" 
                data-page="${i}" 
                ${isActive ? 'disabled' : ''}>
          ${i}
        </button>
      `;
    }

    const prevBtn = this.currentPage > 1 ? `
      <button class="pagination-btn pagination-prev" data-page="${this.currentPage - 1}">
        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M6.5 0.5L0.5 5L6.5 9.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    ` : '';

    const nextBtn = this.currentPage < totalPages ? `
      <button class="pagination-btn pagination-next" data-page="${this.currentPage + 1}">
        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M0.5 0.5L6.5 5L0.5 9.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    ` : '';

    return `
      <div class="faq-pagination">
        ${prevBtn}
        <div class="pagination-pages">${pages}</div>
        ${nextBtn}
      </div>
    `;
  }

  renderEmpty() {
    return `
      <div class="faq-empty tw-text-[#0000004D]">
        <p>No results found</p>
        ${this.currentKeyword ? `<p>Try other keywords</p>` : ''}
      </div>
    `;
  }
}

// ============================================
// 自动初始化
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-documentation-controller]').forEach(container => {
    const mode = container.dataset.mode;
    
    if (mode === 'faq') {
      const faqDataEl = document.getElementById('faq-data-processed');
      if (faqDataEl) {
        try {
          const faqData = JSON.parse(faqDataEl.textContent);
          new FAQController(container, faqData);
        } catch (e) {
          console.error('FAQ data parse error:', e);
        }
      }
    } else {
      new DocumentationController(container);
    }
  });
});
